import React, { FC, ReactNode, useState } from 'react';
import { Button, ClickAwayListener, Paper, Popper as MUIPopper, PopperProps, useTheme } from '@mui/material';
import './Popper.styles.scss';

interface Props extends Required<Pick<PopperProps, 'anchorEl'>> {
  id: string;
  children: ReactNode;
  onSubmit?: (event: React.MouseEvent<HTMLElement>) => void | Promise<void>;
  onClose?: () => void;
  submitText?: string;
  closeText?: string;
  className?: string;
}

export const Popper: FC<Props> = ({
  anchorEl,
  id: propId,
  onSubmit,
  onClose,
  children,
  submitText = 'Submit',
  closeText = 'Cancel',
  className = '',
  ...props
}) => {
  const theme = useTheme();
  const [arrowRef, setArrowRef] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const id = open ? propId : undefined;

  const handleClickAway = () => {
    if (!onClose) {
      return;
    }

    onClose();
  };

  if (!open) {
    return null;
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <MUIPopper
        {...props}
        className={`${className} popper`}
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement="top"
        modifiers={[
          {
            name: 'offset',
            enabled: true,
            options: {
              offset: [0, 8],
            },
          },
          {
            name: 'flip',
            enabled: false,
          },
          {
            name: 'arrow',
            enabled: true,
            options: {
              element: arrowRef,
            },
          },
        ]}
      >
        <Paper className="popper-content p-3">
          <div className={`arrow shadow ${theme.palette.mode}`} ref={setArrowRef} />
          {children}
          {(onClose || onSubmit) && (
            <div className="mt-3 d-flex align-items-center justify-content-end">
              {onClose && (
                <Button
                  variant="text"
                  color="primary"
                  size="small"
                  onClick={onClose}
                  sx={{ color: 'text.primary' }}
                  className="me-2"
                >
                  {closeText}
                </Button>
              )}
              {onSubmit && (
                <Button variant="contained" color="error" size="small" onClick={onSubmit}>
                  {submitText}
                </Button>
              )}
            </div>
          )}
        </Paper>
      </MUIPopper>
    </ClickAwayListener>
  );
};
